import { useCallback } from "react";

import {
  useQuery,
  useQueryClient,
  UseQueryOptions
} from "@tanstack/react-query";

import { GetBulkIsochronePayload } from "@/types/isochorne";
import {
  CatchmentSettings,
  ECatchmentInputBy,
  ECatchmentTransport,
  ERoadAccessMeasurement,
  IsochroneData
} from "@/types/spatial";
import { getEffectiveRoadExclusions, iterateRows } from "@/utils/catchment";
import { groupIsochroneByCoordinate } from "@/utils/isochrone";

import { getBulkIsochrone } from "./isochroneApi";

export const ISOCHORNE_KEYS = {
  isochrone: ["get-isochrone"] as const
} as const;

const isValidGetIsochronePayload = (payload: CatchmentSettings) => {
  const config = payload.roadAccessConfig;
  const datasets = payload.datasetConfig?.datasets ?? {};

  if (
    (!payload.placeCoordinates?.length && !Object.values(datasets)?.length) ||
    !config?.routingType ||
    !config.measurementType
  ) {
    return false;
  }

  // Check if distances or durations array exists, is not empty, and contains valid values
  if (config.measurementType === ERoadAccessMeasurement.DISTANCE) {
    return !!(
      config.distances?.length &&
      config.distances.length > 0 &&
      config.distances.some(
        (distance) => distance != null && String(distance).trim() !== ""
      )
    );
  } else {
    return !!(
      config.durations?.length &&
      config.durations.length > 0 &&
      config.durations.some(
        (duration) => duration != null && String(duration).trim() !== ""
      )
    );
  }
};

/**
 * One request per distinct coordinate — multiple locations can share a point —
 * regrouped onto every caller position afterwards. The BE de-dupes too; this
 * keeps the rings correct if the FE ships first.
 */
const fetchGroupedIsochrone = async (
  payload: GetBulkIsochronePayload
): Promise<IsochroneData[]> => {
  const [isochroneCollection] = await getBulkIsochrone({
    ...payload,
    coordinates: [...new Set(payload.coordinates)]
  });

  return groupIsochroneByCoordinate(isochroneCollection, payload.coordinates);
};

export const useGetBulkIsochroneQuery = (
  isNewRoadExclusionEnabled: boolean,
  payload: CatchmentSettings,
  options?: Omit<
    UseQueryOptions<IsochroneData[], Error, IsochroneData[]>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: [
      isNewRoadExclusionEnabled,
      ISOCHORNE_KEYS.isochrone,
      payload.catchmentInputBy,
      payload.roadAccessConfig?.distances,
      payload.roadAccessConfig?.durations,
      payload.roadAccessConfig?.measurementType,
      payload.roadAccessConfig?.routingType,
      payload.roadAccessConfig?.roadExclusions,
      payload.placeCoordinates,
      payload.datasetConfig
    ],
    queryFn: async () => {
      if (!isValidGetIsochronePayload(payload)) return [];

      const config = payload.roadAccessConfig;
      const metric = config?.measurementType as ERoadAccessMeasurement;

      const contours =
        metric === ERoadAccessMeasurement.DURATION
          ? config?.durations?.map(Number)
          : config?.distances?.map(Number);

      const getCoordinates = (): string[] => {
        if (payload.catchmentInputBy === ECatchmentInputBy.BY_LOCATION) {
          return (
            payload.placeCoordinates?.map(
              (coord) => `${coord.lng},${coord.lat}`
            ) ?? []
          );
        }

        if (payload.catchmentInputBy === ECatchmentInputBy.BY_DATASET) {
          const datasets = payload.datasetConfig?.datasets ?? {};
          const results: string[] = [];
          iterateRows(Object.values(datasets), (long, lat) => {
            results.push(`${long},${lat}`);
          });
          return results;
        }

        return [];
      };

      const coordinates = getCoordinates();
      if (!coordinates.length) return [];

      try {
        return await fetchGroupedIsochrone({
          profile: config?.routingType as ECatchmentTransport,
          contour_metric: metric,
          contours: contours ?? [],
          excludes: getEffectiveRoadExclusions(
            config?.roadExclusions,
            config?.routingType,
            isNewRoadExclusionEnabled
          ),
          coordinates
        });
      } catch (error) {
        console.error("Isochrone fetch error:", error);
        return [{ data: [], position: 1, error: true }];
      }
    },
    ...options
  });
};

/**
 * Imperative twin of `useGetBulkIsochroneQuery`, for callers that resolve a
 * shape outside of render — the agent workspace sync, which is handed a
 * catchment definition and has to re-request its polygon.
 *
 * Cached on the payload, so the same definition asked for twice costs one
 * request.
 */
export const useFetchBulkIsochrone = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (payload: GetBulkIsochronePayload): Promise<IsochroneData[]> =>
      queryClient.fetchQuery({
        queryKey: [
          ISOCHORNE_KEYS.isochrone,
          payload.profile,
          payload.contour_metric,
          payload.contours,
          payload.excludes,
          payload.coordinates
        ],
        queryFn: () => fetchGroupedIsochrone(payload),
        staleTime: Infinity,
        gcTime: Infinity
      }),
    [queryClient]
  );
};
